import {
  addDays,
  addHours,
  endOfDay,
  format,
  getDay,
  isSameDay,
  startOfDay
} from 'date-fns'
import {observer} from 'mobx-react-lite'
import React, {useEffect} from 'react'
import tw from 'tailwind-styled-components'
import {Lang} from 'yougile-lang'
import {minTaskHeight} from '@components/calendar/utils'
import {LinkButton} from '@components/common/button/button'
import {noPropagate} from '@containers/hooks'
import {cn} from '@containers/html-utils'
import {Shift, useCalendarStore} from '../../calendar-store'
import {useDraggableStore} from '../draggable/draggable-store'
import {TaskContent} from '../task/task-content'
import {AllDayPlaceholder} from './all-day-placeholder'
import {dateFormat, DayHeadTw, taskHeight, TasksAllDay} from './all-day-tasks'
import {
  getPointerEventsNone,
  getZIndex,
  minNumberOfTasks
} from './all-day-utils'

const AllDayTaskTw = tw.div`
  absolute
  w-full
  z-10
  hover:z-20

`

const DayHeadUnderTw = tw.div`
  flex
  flex-col
  min-w-200
  grow-0
  basis-1/7
  relative
`

interface Props {
  allTasks: TasksAllDay[]
  tasksForMonday: TasksAllDay[]
  start: Date
  shift: Shift
  width: number
  topPlacementHeight: number
}

export const AllDay = observer(
  React.forwardRef(
    // eslint-disable-next-line max-lines-per-function
    (
      {
        allTasks,
        tasksForMonday,
        start,
        shift,
        width,
        topPlacementHeight
      }: Props,
      ref: React.MutableRefObject<HTMLElement>
    ) => {
      const [state, store] = useCalendarStore()
      const [draggableState] = useDraggableStore()

      const height =
        draggableState.draggedItem.taskType === 'from-hour-to-day' &&
        topPlacementHeight < minTaskHeight
          ? minTaskHeight
          : topPlacementHeight

      const lastTop = allTasks[allTasks.length - 1]?.top ?? 0
      const tasksInCol = lastTop / taskHeight + 1

      useEffect(() => {
        if (height !== state.allDayTasksHeight) {
          if (
            draggableState.draggedItem.taskType === 'from-hour-to-day' &&
            height < minTaskHeight
          ) {
            store.setAllDayTasksHeight(minTaskHeight)
          } else {
            store.setAllDayTasksHeight(height)
          }
        }
      }, [height, draggableState.draggedItem.taskType])

      useEffect(() => {
        state.maxTasksInColHeight !== height &&
          store.setMaxTasksInColHeight(height)
      })

      const limitedTasks =
        state.showMoreAllDayTasks || tasksInCol <= minNumberOfTasks
          ? allTasks
          : allTasks.filter(
              t => (t.top ?? 0) < (minNumberOfTasks - 1) * taskHeight
            )

      return (
        <DayHeadTw
          ref={ref}
          data-day={addHours(startOfDay(start), 12)}
          onClick={() => {
            store.showedPopupButton(true, startOfDay(start))
            store.setOpenedAllDayPopup(true)
          }}
        >
          <DayHeadUnderTw
            style={{height}}
            data-day={addHours(startOfDay(start), 12)}
          >
            {shift === 'current' && (
              <AllDayPlaceholder start={start} width={width} />
            )}
            {tasksForMonday
              .filter(el => el.allDayType === 'multiple-days-for-monday')
              .map(task => {
                if (
                  task.roundedStartDate < store.startDate('current') &&
                  task.roundedDeadline > endOfDay(store.endDate('current')) &&
                  getDay(start) === 1
                ) {
                  return (
                    <AllDayTaskTw
                      className={cn(getPointerEventsNone(draggableState, task))}
                      style={{
                        top: task.top,
                        width: width * 7,
                        height: taskHeight,
                        zIndex: getZIndex(draggableState, task)
                      }}
                      key={task.task.id}
                    >
                      <TaskContent
                        wFull
                        height={taskHeight}
                        allDayWidth={width * task.duration}
                        allDayTop={task.top}
                        task={task}
                        type="all-day-multiple"
                        allDay
                        className="border-l-0 border-r-0 rounded-l-none rounded-r-none"
                      />
                    </AllDayTaskTw>
                  )
                } else if (getDay(start) === 1) {
                  return (
                    <AllDayTaskTw
                      className={cn(getPointerEventsNone(draggableState, task))}
                      style={{
                        top: task.top,
                        width: width * task.duration,
                        height: taskHeight,
                        zIndex: getZIndex(draggableState, task)
                      }}
                      key={task.task.id}
                    >
                      <TaskContent
                        wFull
                        height={taskHeight}
                        allDayWidth={width * task.duration}
                        allDayTop={task.top}
                        task={task}
                        type="all-day-multiple"
                        allDay
                        allDayDrag
                        className="border-l-0 rounded-l-none"
                      />
                    </AllDayTaskTw>
                  )
                }
                return null
              })}
            {tasksForMonday
              .filter(el => el.allDayType === 'long-with-time-for-monday')
              .map(task => {
                if (
                  task.roundedStartDate < store.startDate('current') &&
                  task.roundedDeadline > endOfDay(store.endDate('current')) &&
                  getDay(start) === 1
                ) {
                  return (
                    <AllDayTaskTw
                      className={cn(getPointerEventsNone(draggableState, task))}
                      style={{
                        top: task.top,
                        width: width * 7,
                        height: taskHeight,
                        zIndex: getZIndex(draggableState, task)
                      }}
                      key={task.task.id}
                    >
                      <TaskContent
                        wFull
                        height={taskHeight}
                        allDayWidth={width * task.duration}
                        allDayTop={task.top}
                        task={task}
                        type="all-day-with-time"
                        allDay
                        className="border-l-0 border-r-0 rounded-l-none rounded-r-none"
                      />
                    </AllDayTaskTw>
                  )
                } else if (getDay(start) === 1) {
                  return (
                    <AllDayTaskTw
                      className={cn(getPointerEventsNone(draggableState, task))}
                      style={{
                        top: task.top,
                        width: width * task.duration,
                        height: taskHeight,
                        zIndex: getZIndex(draggableState, task)
                      }}
                      key={task.task.id}
                    >
                      <TaskContent
                        wFull
                        height={taskHeight}
                        allDayWidth={width * task.duration}
                        allDayTop={task.top}
                        task={task}
                        type="all-day-multiple"
                        allDay
                        allDayDrag
                        className="border-l-0 rounded-l-none"
                      />
                    </AllDayTaskTw>
                  )
                }
                return null
              })}
            {limitedTasks
              .filter(el => el.allDayType === 'multiple-days')
              .map(task => {
                if (
                  format(task.roundedStartDate, dateFormat) ===
                  format(start, dateFormat)
                ) {
                  return (
                    <AllDayTaskTw
                      className={cn(getPointerEventsNone(draggableState, task))}
                      style={{
                        top: task.top,
                        width: width * task.duration,
                        height: taskHeight,
                        zIndex: getZIndex(draggableState, task)
                      }}
                      key={task.task.id}
                    >
                      <TaskContent
                        wFull
                        height={taskHeight}
                        allDayWidth={width * task.duration}
                        allDayTop={task.top}
                        task={task}
                        type="all-day-multiple"
                        allDay
                        allDayDrag
                      />
                    </AllDayTaskTw>
                  )
                }

                return null
              })}
            {limitedTasks
              .filter(el => el.allDayType === 'single-day')
              .map(task => {
                return (
                  <AllDayTaskTw
                    className={cn(getPointerEventsNone(draggableState, task))}
                    style={{
                      top: task.top,
                      height: taskHeight,
                      zIndex:
                        draggableState.draggedItem.id &&
                        draggableState.draggedItem.id === task.task.id &&
                        draggableState.draggedItem.changeAction ===
                          'change-deadline'
                          ? 20
                          : undefined
                    }}
                    key={task.task.id}
                  >
                    <TaskContent
                      wFull
                      height={taskHeight}
                      allDayWidth={width}
                      allDayTop={task.top}
                      task={task}
                      type="all-day-single"
                      allDay
                      allDayDrag
                      allDayDragWithoutStartDate
                      dragWithoutStartDate
                    />
                  </AllDayTaskTw>
                )
              })}
            {limitedTasks
              .filter(el => el.allDayType === 'long-with-time')
              .map(task => {
                if (
                  format(addDays(task.roundedStartDate, 1), dateFormat) ===
                    format(start, dateFormat) &&
                  !isSameDay(task.roundedDeadline, start) &&
                  !isSameDay(task.roundedStartDate, start) &&
                  task.roundedStartDate < start &&
                  task.roundedDeadline > start
                ) {
                  return (
                    <AllDayTaskTw
                      className={cn(getPointerEventsNone(draggableState, task))}
                      style={{
                        top: task.top,
                        width: width * task.duration,
                        height: taskHeight,
                        zIndex: getZIndex(draggableState, task)
                      }}
                      key={task.task.id}
                    >
                      <TaskContent
                        wFull
                        height={taskHeight}
                        allDayWidth={width * task.duration}
                        allDayTop={task.top}
                        task={task}
                        type="all-day-with-time"
                        allDay
                      />
                    </AllDayTaskTw>
                  )
                }
                return null
              })}
            {tasksInCol > minNumberOfTasks && !state.showMoreAllDayTasks && (
              <LinkButton
                onClick={e => {
                  noPropagate(e)
                  store.toggleShowMoreAllDayTasks()
                }}
                className="mt-88 text-third-old hover:text-action-hover active:text-action-pressed"
              >
                {Lang.msg.calendar.showMore} {lastTop / taskHeight - 2} »
              </LinkButton>
            )}
          </DayHeadUnderTw>
        </DayHeadTw>
      )
    }
  )
)
